import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Text,
} from '@musa.tech/design-system';
import { FaturamentoApiError, type DuplicatePoPrevious } from '@/integrations/faturamento/client';
import { format, parseISO } from 'date-fns';

/** Limite da PO — a mesma regra do serviço (1 a 50 caracteres depois do trim). */
export const PO_MAX_LENGTH = 50;

const GENERIC_ERROR = 'Não foi possível aprovar. Tente novamente.';

interface ApproveMeasurementPoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Aprova com a PO já aparada. Rejeita com `FaturamentoApiError`: `PO_DUPLICATE` vira o aviso
   * de PO repetida, o resto vira a mensagem genérica — o pai só cuida do que acontece no sucesso.
   */
  onSubmit: (input: { po_number: string; confirm_duplicate: boolean }) => Promise<void>;
}

function formatPeriod(previous: DuplicatePoPrevious): string {
  const day = (iso: string) => {
    try {
      return format(parseISO(iso), 'dd/MM/yyyy');
    } catch {
      return iso;
    }
  };
  return `${day(previous.period_start)} a ${day(previous.period_end)}`;
}

/**
 * Aprovação de medição de cliente que exige PO (EDD-582): o gerador informa o número antes de
 * liberar a medição para o faturamento. O número vai impresso na nota.
 *
 * Cliente que não exige PO não passa por aqui — segue no `AlertDialog` de confirmação de sempre.
 */
export function ApproveMeasurementPoDialog({
  open,
  onOpenChange,
  onSubmit,
}: ApproveMeasurementPoDialogProps) {
  const [po, setPo] = useState('');
  const [duplicate, setDuplicate] = useState<DuplicatePoPrevious | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fechar descarta o rascunho: reabrir com a PO da tentativa anterior esconderia o que foi digitado.
  useEffect(() => {
    if (!open) {
      setPo('');
      setDuplicate(null);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const trimmed = po.trim();
  const tooLong = trimmed.length > PO_MAX_LENGTH;
  const isValid = trimmed.length > 0 && !tooLong;

  const submit = async (confirmDuplicate: boolean) => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({ po_number: trimmed, confirm_duplicate: confirmDuplicate });
    } catch (err) {
      if (err instanceof FaturamentoApiError && err.isPoDuplicate && err.previous) {
        setDuplicate(err.previous);
      } else if (err instanceof FaturamentoApiError && err.isPoInvalid) {
        setError('Informe o número da PO com até 50 caracteres.');
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !isSubmitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Informe a PO para aprovar a medição</DialogTitle>
          <DialogDescription>
            Este cliente exige Pedido de Compra. O número vai impresso na nota. Ao aprovar, a
            medição segue para faturamento e o prazo para contestar encerra agora.
          </DialogDescription>
        </DialogHeader>

        {duplicate ? (
          <div className="space-y-4">
            <Alert variant="warning">
              <AlertDescription>
                Esta PO já foi informada na medição de {formatPeriod(duplicate)} –{' '}
                {duplicate.client_name}.
              </AlertDescription>
            </Alert>
            <Text variant="body" size="small" className="text-neutral-700">
              Número informado: <strong>{trimmed}</strong>. Siga se a PO vale para as duas
              medições, ou corrija o número.
            </Text>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicate(null)} disabled={isSubmitting}>
                Corrigir
              </Button>
              <Button onClick={() => void submit(true)} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Seguir com esta PO
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={event => {
              event.preventDefault();
              void submit(false);
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="measurement-po">Número da PO</Label>
              <Input
                id="measurement-po"
                value={po}
                onChange={event => setPo(event.target.value)}
                aria-invalid={tooLong}
                aria-describedby={tooLong ? 'measurement-po-error' : undefined}
                autoComplete="off"
                autoFocus
              />
              {tooLong && (
                <Text id="measurement-po-error" variant="body" size="small" className="text-destructive-700">
                  A PO tem {trimmed.length} caracteres — o limite é {PO_MAX_LENGTH}.
                </Text>
              )}
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Voltar
              </Button>
              <Button type="submit" disabled={!isValid || isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Aprovar medição
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
